import {
  GetAsync,
  getToken,
  PostAsync,
  PostFileAsync,
} from "src/common/httpRequests";
import { ASK_DOST_URL } from "src/constants/api";
import { API_GATEWAY } from "src/constants/settings";
import { store } from "@redux/store";
import { IConfirmDecision, ISendChatMessage } from "../types";

const ENDPOINT = `${API_GATEWAY}/${ASK_DOST_URL}`;

let isLoginInProgress = false;

const getUserIdFromToken = (token: string | null) => {
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.oid || payload.sub || payload.userId || payload.id;
  } catch (e) {
    console.error("Error", e);
    return undefined;
  }
};

const loginToAskDost = async () => {
  const { refreshToken: refresh_token, firstName: organization_name } =
    store.getState()?.credential?.userCred;

  if (isLoginInProgress) return;
  isLoginInProgress = true;
  try {
    const organizationId = localStorage.getItem("company_id");
    const payload: any = {
      refresh_token,
      organization_id: organizationId,
      organization_name,
    };

    const res = await PostAsync(
      `${ENDPOINT}/api/login-token`,
      payload,
      true,
      { withCredentials: true },
      true,
    );
    return res;
  } finally {
    isLoginInProgress = false;
  }
};

type TAuthRetryOptions<T> = {
  onRetry?: () => Promise<T>;
  shouldRetry?: (res: T) => boolean;
};

const withAuthRetry = async <T extends { status?: number }>(
  request: () => Promise<T>,
  {
    onRetry,
    shouldRetry = (res) => res?.status === 401,
  }: TAuthRetryOptions<T> = {},
): Promise<T> => {
  const res = await request();
  if (shouldRetry(res)) {
    await loginToAskDost();
    return (onRetry ?? request)();
  }
  return res;
};

const streamChatMessage = async (data: ISendChatMessage) => {
  const response = await withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/chat`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: authorization,
      },
      body: JSON.stringify(data),
    });
  });

  if (!response.ok) {
    try {
      const errorData = await response.json();
      return {
        ...response,
        status: response.status,
        data: errorData,
        message: errorData?.detail || "An error occurred",
      };
    } catch {
      return {
        ...response,
        status: response.status,
        message: response.statusText,
      };
    }
  }

  return response;
};

const sendConfirmDecision = (data: IConfirmDecision) =>
  withAuthRetry(
    () =>
      PostAsync(
        `${ENDPOINT}/confirm`,
        data,
        true,
        { withCredentials: true },
        true,
      ),
    {
      onRetry: () =>
        PostAsync(
          `${ENDPOINT}/confirm`,
          data,
          true,
          { withCredentials: true },
          true,
        ),
    },
  );

const resetChat = () =>
  withAuthRetry(() =>
    PostAsync(`${ENDPOINT}/reset`, {}, true, { withCredentials: true }, true),
  );

const uploadFile = (formData: FormData) => {
  const refreshToken = localStorage.getItem("token");
  const userId = getUserIdFromToken(refreshToken);

  if (refreshToken) {
    formData.append("refresh_token", refreshToken);
  }
  if (userId) {
    formData.append("user_id", userId);
  }

  return withAuthRetry(
    () =>
      PostFileAsync(
        `${ENDPOINT}/upload`,
        formData,
        true,
        { withCredentials: true },
        true,
      ),
    { shouldRetry: (res) => res?.status === 401 || res?.status === 403 },
  );
};

const getConversations = () =>
  withAuthRetry(() =>
    GetAsync(`${ENDPOINT}/conversations`, true, undefined, {
      withCredentials: true,
    }),
  );

const getConversationHistory = (conversationId: string) =>
  withAuthRetry(() =>
    GetAsync(`${ENDPOINT}/conversations/${conversationId}`, true, undefined, {
      withCredentials: true,
    }),
  );

const resumeConversation = (conversationId: string) =>
  withAuthRetry(() =>
    PostAsync(
      `${ENDPOINT}/conversations/${conversationId}/resume`,
      {},
      true,
      { withCredentials: true },
      true,
    ),
  );

const deleteConversation = (conversationId: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/conversations/${conversationId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });
  });

const renameConversation = (conversationId: string, title: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/conversations/${conversationId}`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
      body: JSON.stringify({ title }),
    });
  });

const getMemories = () =>
  withAuthRetry(() =>
    GetAsync(`${ENDPOINT}/memory`, true, undefined, { withCredentials: true }),
  );

const forgetPattern = (patternId: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/memory/${patternId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });
  });

const forgetEverything = () =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/memory`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });
  });

const downloadFile = (fileId: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/files/${fileId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: authorization,
      },
    });
  });

const getSavedPrompts = () =>
  withAuthRetry(() =>
    GetAsync(`${ENDPOINT}/prompts`, true, undefined, {
      withCredentials: true,
    }),
  );

const savePrompt = (payload: { text: string }) =>
  withAuthRetry(() =>
    PostAsync(
      `${ENDPOINT}/prompts`,
      payload,
      true,
      { withCredentials: true },
      true,
    ),
  );

const deleteSavedPrompt = (promptId: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/prompts/${promptId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });
  });

const getErpConnectionStatus = () =>
  withAuthRetry(() =>
    GetAsync(`${ENDPOINT}/erp/connection`, true, undefined, {
      withCredentials: true,
    }),
  );

const connectErp = (payload: {
  url: string;
  username: string;
  api_key: string;
  db_name?: string;
}) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/erp/connection`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
      body: JSON.stringify(payload),
    });
  });

const disconnectErp = () =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/erp/connection`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
    });
  });

const syncErp = (onEvent?: (data: any) => void) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    const response = await fetch(`${ENDPOINT}/erp/connection/sync`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: authorization,
      },
    });

    if (!response.ok) {
      return { status: response.status, data: null };
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder("utf-8");
    let finalData = null;

    if (reader) {
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; 

        let currentEvent = "";
        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith("data:")) {
            const dataStr = line.substring(5).trim();
            if (dataStr) {
              try {
                const parsedData = JSON.parse(dataStr);
                if (onEvent) onEvent(parsedData);
                if (currentEvent === "done" || parsedData.type === "done") {
                  finalData = parsedData;
                }
              } catch (e) {
                console.error("Failed to parse SSE data", e);
              }
            }
          }
        }
      }
    }
    return { status: 200, data: finalData || {} };
  });

const previewDocumentHandle = (handle: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/preview/${handle}`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: authorization,
      },
    });
  });

const getPdfPreviewFile = (handle: string) =>
  withAuthRetry(async () => {
    const authorization = await getToken();
    return fetch(`${ENDPOINT}/preview/${handle}/pdf`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: authorization,
      },
    });
  });

export {
  loginToAskDost,
  resetChat,
  sendConfirmDecision,
  streamChatMessage,
  uploadFile,
  getConversations,
  getConversationHistory,
  resumeConversation,
  deleteConversation,
  renameConversation,
  getMemories,
  forgetPattern,
  forgetEverything,
  downloadFile,
  getSavedPrompts,
  savePrompt,
  deleteSavedPrompt,
  getErpConnectionStatus,
  connectErp,
  disconnectErp,
  syncErp,
  previewDocumentHandle,
  getPdfPreviewFile,
};